<script lang="ts">
import { LEGAL_LINKS } from "@doc-cheap/legal";
import { PRICE_HEADLINE } from "$lib/pricing/copy";
import { AUTHOR_NAME } from "$lib/seo/jsonld";
import { OPERATOR_ADDRESS, OPERATOR_EMAIL, OPERATOR_NAME } from "$lib/seo/operator";
import SeoHead from "$lib/seo/SeoHead.svelte";
import SiteFooter from "$lib/seo/SiteFooter.svelte";
import { DOCS_URL } from "$lib/seo/site";

// Who is behind the service, and how to reach them.
//
// A page like this is not decoration on a developer tool. Somebody deciding
// whether to send other people's identity documents to an API wants to know
// which company receives them, where that company is established and what it
// has committed to in writing — and if the site does not say, the answer they
// reach is not a good one. Everything here is a fact stated in the published
// legal texts; nothing is a claim made only on this page.

const title = "About doc.cheap – who runs the document recognition API";
const description =
  `doc.cheap is operated by ${OPERATOR_NAME}, established in Iceland. What the service does, ` +
  "what it deliberately does not do, and where the terms are.";
</script>

<SeoHead {title} {description} path="/about" />

<div>
  <main class="mx-auto max-w-3xl px-6 py-12">
    <h1 class="text-3xl font-semibold tracking-tight sm:text-4xl">About doc.cheap</h1>

    <p class="mt-5 text-lg text-slate-600">
      doc.cheap reads passports, identity cards and driver licences from a photograph and returns
      the fields as JSON, in one HTTP request. It is built for developers who need that one thing
      and do not want an onboarding call to get it: the first call needs no account, and a
      recognised document costs {PRICE_HEADLINE}.
    </p>

    <section class="mt-12" aria-labelledby="operator">
      <h2 id="operator" class="text-2xl font-semibold tracking-tight">Who operates the service</h2>
      <address class="mt-4 text-slate-700 not-italic">
        <strong class="font-semibold text-slate-900">{OPERATOR_NAME}</strong><br />
        {#each OPERATOR_ADDRESS as line (line)}
          {line}<br />
        {/each}
        <a href="mailto:{OPERATOR_EMAIL}" class="underline hover:text-slate-900">{OPERATOR_EMAIL}</a>
      </address>
      <p class="mt-4 text-slate-600">
        That is the operator named in the Terms of Service and the address legal notices are sent
        to. It is the same company whatever page of this site you are on: there is no reseller, no
        white label and no second entity handling the data.
      </p>
    </section>

    <section class="mt-12" aria-labelledby="author">
      <h2 id="author" class="text-2xl font-semibold tracking-tight">Who writes the blog</h2>
      <p class="mt-4 text-slate-600">
        The articles on the <a href="/blog" class="underline hover:text-slate-900">blog</a> are
        written by {AUTHOR_NAME}. Each one names him at the top, and every claim an article makes
        about the product is checked against the product before it is published.
      </p>
    </section>

    <section class="mt-12" aria-labelledby="what-we-do">
      <h2 id="what-we-do" class="text-2xl font-semibold tracking-tight">
        What the service does, and what it does not
      </h2>
      <p class="mt-4 text-slate-600">
        It performs recognition: it reads what is printed on the document and reports it, with the
        machine-readable zone checked against its own ICAO 9303 check digits and every field
        carrying a confidence band.
      </p>
      <p class="mt-4 text-slate-600">
        It does not perform authentication. Nothing here inspects a document's security features,
        and the response says so in a field rather than staying silent about it. A zone whose check
        digits pass is internally consistent; it is not evidence that the document is genuine.
      </p>
      <p class="mt-4 text-slate-600">
        The uploaded image is held in memory for the length of the request and is never written to
        disk. The result can be kept for a window you choose, down to nothing at all.
      </p>
    </section>

    <section class="mt-12" aria-labelledby="commitments">
      <h2 id="commitments" class="text-2xl font-semibold tracking-tight">What is in writing</h2>
      <p class="mt-4 text-slate-600">
        The three published texts are the whole agreement – there are no individually negotiated
        contracts, and nothing on a marketing page overrides them.
      </p>
      <ul class="mt-4 space-y-2 text-slate-700">
        {#each LEGAL_LINKS as link (link.id)}
          <li>
            <a href={link.href} class="underline hover:text-slate-900">{link.label}</a>
          </li>
        {/each}
        <li>
          <a href="/status" class="underline hover:text-slate-900">Service status</a> – uptime,
          processing times and the incident history, published rather than described.
        </li>
        <li>
          <a href={DOCS_URL} rel="external" class="underline hover:text-slate-900">Documentation</a>
          – the API reference, the guides and the changelog.
        </li>
      </ul>
    </section>

    <section class="mt-12" aria-labelledby="get-in-touch">
      <h2 id="get-in-touch" class="text-2xl font-semibold tracking-tight">Getting in touch</h2>
      <p class="mt-4 text-slate-600">
        One address, read by the people who build the service:
        <a href="mailto:{OPERATOR_EMAIL}" class="underline hover:text-slate-900">{OPERATOR_EMAIL}</a>.
        <a href="/contact" class="underline hover:text-slate-900">What to include</a> depends on what
        you are writing about.
      </p>
    </section>
  </main>

  <SiteFooter />
</div>
